export type SentryEventLike = {
  exception?: {
    values?: Array<{
      type?: string;
      value?: string;
    }>;
  };
  message?: string;
};

const EXPECTED_ABORT_ERROR_PATTERNS = [
  /^AbortError\b/i,
  /\bFetch is aborted\b/i,
  /\bsignal is aborted\b/i,
  /\brequest aborted\b/i,
];

const EXTERNAL_IN_APP_BROWSER_ERROR_PATTERNS = [
  /Error invoking postMessage: Java object is gone/i,
  /iabjs:\/\/navigation_performance_logger_android/i,
  // UAE Pass's hosted pages ship a WKWebView native-bridge script
  // (sendDataToNative / sendPageHideMessage) that dereferences
  // window.webkit.messageHandlers. In ordinary browsers (Instagram/Safari
  // in-app views) there is no webkit bridge, so their script throws. It is
  // third-party code we cannot fix: pure noise for us.
  /window\.webkit\.messageHandlers/i,
  /sendDataToNative/i,
  /sendPageHideMessage/i,
];

const EXPECTED_RESUME_HANDOFF_ERROR_PATTERNS = [
  /\bVISION_EXTRACT_FAILED\b/i,
  /\bUNREADABLE_FILE\b/i,
  /\bUNSUPPORTED_FILE_TYPE\b/i,
  /\bWORD_EXTRACT_FAILED\b/i,
  /\bVision extraction returned no text\b/i,
  /\bcould not extract meaningful text from file\b/i,
  /\bcould not extract content from file\b/i,
  /\bcould not extract text from word document\b/i,
  /\bunsupported file type\b/i,
];

function eventTexts(event: SentryEventLike) {
  return [
    event.message,
    ...(event.exception?.values ?? []).flatMap((value) => [
      value.type,
      value.value,
    ]),
  ].filter((value): value is string => typeof value === 'string');
}

function matchesAny(texts: string[], patterns: RegExp[]) {
  return texts.some((text) => patterns.some((pattern) => pattern.test(text)));
}

function hasExpectedResumeHandoffError(event: SentryEventLike) {
  return (event.exception?.values ?? []).some((value) => {
    if (value.type !== 'ResumeHandoffClientError') {
      return false;
    }

    return matchesAny(
      [value.value, event.message].filter(
        (text): text is string => typeof text === 'string',
      ),
      EXPECTED_RESUME_HANDOFF_ERROR_PATTERNS,
    );
  });
}

export function shouldDropExpectedClientError(event: SentryEventLike) {
  const texts = eventTexts(event);
  if ((event.exception?.values ?? []).some((value) => value.type === 'AbortError')) {
    return true;
  }

  return (
    matchesAny(texts, EXPECTED_ABORT_ERROR_PATTERNS) ||
    matchesAny(texts, EXTERNAL_IN_APP_BROWSER_ERROR_PATTERNS) ||
    hasExpectedResumeHandoffError(event)
  );
}
